import { useSelector } from "react-redux";
import { actionCentreRegistry } from "./ActionCentre/registry";
import { useHomeOverallCount } from "./HomeOverallCountContext";
import { isRequestTypeAccessible } from "./ActionCentre/requestTypeAccess";

// Shared source of the employee's own pending requests for the Home dashboard.
// Both the "Pending Requests" KPI tile and the "My Requests Summary" card read
// from this, and it is now backed by the single consolidated overall-count
// endpoint (via HomeOverallCountProvider) instead of fanning out to one request
// per My Zone > Apply type. The count in the tile and the per-type breakdown in
// the summary come from the same fetch, so they can never drift apart.
export const useMyPendingRequests = () => {
  const { countsByType, itemsByType, loading } = useHomeOverallCount();
  const { userScreenAccess } = useSelector((state) => state.tokenReducer);

  // The consolidated overall-count endpoint has no concept of this user's own
  // per-submodule viewer permission, so its counts/items cover every registry
  // type regardless of whether this role can actually see it in My Requests
  // (e.g. Leave's Pending count/items still came through here with Leaves'
  // viewer permission off, even though that's exactly what already hides
  // Leave from the My Requests navigation). Filtered here, once, so the
  // Pending Requests KPI total, the My Requests Summary rows and this
  // flattened `pending` list all agree with each other and with what My
  // Requests itself shows -- same check MyRequestsSummarySection uses to
  // hide its own rows.
  const accessibleTypes = actionCentreRegistry.filter((entry) =>
    isRequestTypeAccessible(userScreenAccess?.module_details, entry.type),
  );

  const normalizedCounts = {};
  const pending = [];
  accessibleTypes.forEach((entry) => {
    normalizedCounts[entry.type] = Number(countsByType?.[entry.type]) || 0;
    (itemsByType?.[entry.type] || []).forEach((item) =>
      pending.push({ ...item, type: entry.type }),
    );
  });

  return {
    pending,
    loading,
    countsByType: normalizedCounts,
    total: Object.values(normalizedCounts).reduce(
      (sum, count) => sum + count,
      0,
    ),
  };
};
